// SENTINEL: 6_Physics - Carousel 3D (Spatial UI, Animation)
// Vitality: Healthy
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Idea } from '../types';
import { Plus, Search, X, FileText, Sparkles, Edit3, LayoutGrid, ChevronLeft, ChevronRight, Cloud, Brain } from 'lucide-react';

interface Carousel3DProps {
  ideas: Idea[];
  activeIdeaId: string | null;
  onSelectIdea: (id: string | null) => void;
  onCreateIdea: () => void;
  onEditIdea: (id: string) => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  isSearchExpanded: boolean;
  onSearchToggle: (expanded: boolean) => void;
  showTileEdgeColors?: boolean;
  onToggleViewMode?: () => void;
}

const TILE_WIDTH = 190;
const TILE_GAP = 20;
const TILE_STEP = TILE_WIDTH + TILE_GAP + 30;
const VISIBLE_TILES = 9;

const GRADIENT_COLORS = [
  { from: 'rgba(139, 92, 246, 0.9)', to: 'rgba(139, 92, 246, 0)' },  // violet
  { from: 'rgba(6, 182, 212, 0.9)', to: 'rgba(6, 182, 212, 0)' },    // cyan
  { from: 'rgba(16, 185, 129, 0.9)', to: 'rgba(16, 185, 129, 0)' },  // emerald
  { from: 'rgba(245, 158, 11, 0.9)', to: 'rgba(245, 158, 11, 0)' },  // amber
  { from: 'rgba(244, 63, 94, 0.9)', to: 'rgba(244, 63, 94, 0)' },    // rose
];

export const Carousel3D: React.FC<Carousel3DProps> = React.memo(({
  ideas,
  activeIdeaId,
  onSelectIdea,
  onCreateIdea,
  onEditIdea,
  searchQuery,
  onSearchChange,
  isSearchExpanded,
  onSearchToggle,
  showTileEdgeColors = true,
  onToggleViewMode,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartX, setDragStartX] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const [velocity, setVelocity] = useState(0);
  const [lastDragTime, setLastDragTime] = useState(0);
  const [lastDragX, setLastDragX] = useState(0);
  const [glowPulse, setGlowPulse] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const totalItems = ideas.length;
  const maxIndex = Math.max(0, totalItems - 1);

  useEffect(() => {
    if (ideas.length > 0) {
      if (currentIndex >= ideas.length) {
        setCurrentIndex(ideas.length - 1);
      }
      if (activeIdeaId !== null && !ideas.some(idea => idea.id === activeIdeaId)) {
        const clampedIndex = Math.min(currentIndex, ideas.length - 1);
        const newCenteredIdea = ideas[clampedIndex];
        if (newCenteredIdea) {
          onSelectIdea(newCenteredIdea.id);
        }
      }
    } else {
      setCurrentIndex(0);
      if (activeIdeaId !== null) {
        onSelectIdea(null);
      }
    }
  }, [ideas, currentIndex, activeIdeaId, onSelectIdea]);

  useEffect(() => {
    if (activeIdeaId !== null) {
      const idx = ideas.findIndex(idea => idea.id === activeIdeaId);
      if (idx !== -1 && idx !== currentIndex) {
        animateToIndex(idx);
      }
    }
  }, [activeIdeaId, ideas]);

  useEffect(() => {
    const interval = setInterval(() => {
      setGlowPulse(prev => !prev);
    }, 1500);
    return () => clearInterval(interval);
  }, []);

  const animateToIndex = useCallback((targetIndex: number, isUserAction: boolean = false) => {
    const clampedIndex = Math.max(0, Math.min(targetIndex, maxIndex));
    setIsAnimating(true);
    setCurrentIndex(clampedIndex);
    setTimeout(() => setIsAnimating(false), 300);

    if (isUserAction && clampedIndex !== currentIndex && ideas.length > 0) {
      const newIdea = ideas[clampedIndex];
      if (newIdea) {
        onSelectIdea(newIdea.id);
      }
    }
  }, [maxIndex, currentIndex, ideas, onSelectIdea]);

  const goToPrev = useCallback(() => {
    if (currentIndex > 0 && !isAnimating) {
      animateToIndex(currentIndex - 1, true);
    }
  }, [currentIndex, isAnimating, animateToIndex]);

  const goToNext = useCallback(() => {
    if (currentIndex < maxIndex && !isAnimating) {
      animateToIndex(currentIndex + 1, true);
    }
  }, [currentIndex, maxIndex, isAnimating, animateToIndex]);

  const handleWheel = useCallback((e: WheelEvent) => {
    e.preventDefault();
    if (isAnimating) return;

    const delta = e.deltaX !== 0 ? e.deltaX : e.deltaY;
    const direction = delta > 0 ? 1 : -1;

    const sensitivity = 0.4;
    const scrollAmount = Math.abs(delta) * sensitivity;

    if (scrollAmount > 15) {
      animateToIndex(currentIndex + direction, true);
    }
  }, [currentIndex, isAnimating, animateToIndex]);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    setIsDragging(true);
    setDragStartX(e.touches[0].clientX);
    setLastDragX(e.touches[0].clientX);
    setLastDragTime(Date.now());
    setDragOffset(0);
    setVelocity(0);
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isDragging) return;

    const currentX = e.touches[0].clientX;
    const now = Date.now();
    const timeDelta = now - lastDragTime;

    if (timeDelta > 0) {
      const instantVelocity = (currentX - lastDragX) / timeDelta;
      setVelocity(instantVelocity * 0.7 + velocity * 0.3);
    }

    setLastDragX(currentX);
    setLastDragTime(now);
    setDragOffset(currentX - dragStartX);
  }, [isDragging, dragStartX, lastDragX, lastDragTime, velocity]);

  const handleTouchEnd = useCallback(() => {
    if (!isDragging) return;
    setIsDragging(false);

    let indexChange = Math.round(-dragOffset / TILE_STEP);

    if (Math.abs(velocity) > 0.4) {
      indexChange += velocity < 0 ? 1 : -1;
    }

    setDragOffset(0);
    animateToIndex(currentIndex + indexChange, true);
  }, [isDragging, dragOffset, velocity, currentIndex, animateToIndex]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button === 1) {
      e.preventDefault();
      setIsDragging(true);
      setDragStartX(e.clientX);
      setLastDragX(e.clientX);
      setLastDragTime(Date.now());
      setDragOffset(0);
      setVelocity(0);
    }
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      container.removeEventListener('wheel', handleWheel);
    };
  }, [handleWheel]);

  useEffect(() => {
    if (isDragging) {
      const handleGlobalMouseMove = (e: MouseEvent) => {
        const currentX = e.clientX;
        const now = Date.now();
        const timeDelta = now - lastDragTime;

        if (timeDelta > 0) {
          const instantVelocity = (currentX - lastDragX) / timeDelta;
          setVelocity(prev => instantVelocity * 0.7 + prev * 0.3);
        }

        setLastDragX(currentX);
        setLastDragTime(now);
        setDragOffset(currentX - dragStartX);
      };

      const handleGlobalMouseUp = () => {
        setIsDragging(false);

        let indexChange = Math.round(-dragOffset / TILE_STEP);

        if (Math.abs(velocity) > 0.4) {
          indexChange += velocity < 0 ? 1 : -1;
        }

        setDragOffset(0);
        animateToIndex(currentIndex + indexChange, true);
      };

      window.addEventListener('mousemove', handleGlobalMouseMove);
      window.addEventListener('mouseup', handleGlobalMouseUp);

      return () => {
        window.removeEventListener('mousemove', handleGlobalMouseMove);
        window.removeEventListener('mouseup', handleGlobalMouseUp);
      };
    }
  }, [isDragging, dragStartX, lastDragX, lastDragTime, velocity, dragOffset, currentIndex, animateToIndex]);

  const visibleItems = useMemo(() => {
    const result: Array<{ idea: Idea; index: number; position: number }> = [];
    const halfVisible = Math.floor(VISIBLE_TILES / 2);

    for (let pos = -halfVisible; pos <= halfVisible; pos++) {
      const actualIndex = currentIndex + pos;
      if (actualIndex >= 0 && actualIndex < ideas.length) {
        result.push({
          idea: ideas[actualIndex],
          index: actualIndex,
          position: pos
        });
      }
    }

    return result;
  }, [ideas, currentIndex]);

  const getTileStyle = (position: number): React.CSSProperties => {
    const dragAdjust = dragOffset / TILE_STEP;
    const adjustedPosition = position + dragAdjust;

    const absPos = Math.abs(adjustedPosition);

    const rotateY = adjustedPosition * -30;
    const rotateZ = adjustedPosition * 0.5;
    const translateZ = -absPos * 80;
    const translateX = adjustedPosition * TILE_STEP;
    const translateY = absPos * 3;
    const scale = 1 - absPos * 0.10;
    const opacity = 1 - absPos * 0.12;
    const zIndex = 100 - Math.round(absPos * 10);

    const blur = absPos > 2 ? Math.min((absPos - 2) * 1.2, 2) : 0;

    return {
      position: 'absolute',
      left: '50%',
      transform: `
        translateX(-50%) 
        translateX(${translateX}px) 
        translateY(${translateY}px)
        perspective(1200px) 
        rotateY(${rotateY}deg) 
        rotateZ(${rotateZ}deg)
        translateZ(${translateZ}px) 
        scale(${Math.max(0.5, scale)})
      `,
      opacity: Math.max(0.3, opacity),
      zIndex,
      filter: blur > 0 ? `blur(${blur}px)` : 'none',
      transition: isDragging ? 'none' : 'all 0.28s cubic-bezier(0.34, 1.56, 0.64, 1)',
      willChange: 'transform, opacity, filter',
      transformStyle: 'preserve-3d',
      backfaceVisibility: 'hidden',
    };
  };

  const handleTileClick = (idea: Idea, index: number) => {
    if (index === currentIndex) {
      if (idea.id !== activeIdeaId) {
        onSelectIdea(idea.id);
      }
    } else {
      animateToIndex(index, true);
    }
  };

  const handleEditClick = (e: React.MouseEvent, ideaId: string) => {
    e.stopPropagation();
    onEditIdea(ideaId);
  };

  const handleQuickNotesClick = () => {
    onSelectIdea(null);
  };

  const isQuickNotesActive = activeIdeaId === null;

  return (
    <div className="w-full h-28 sm:h-32 relative bg-cc-surface select-none flex flex-col sm:flex-row items-center">

      {/* Fixed Left Section: Create + Search + Quick Notes (hidden on very small screens) */}
      <div className="hidden sm:flex flex-shrink-0 items-center gap-2 pl-3 pr-2 z-[200] relative">
        <button
          onClick={onCreateIdea}
          className="flex items-center justify-center w-10 h-20 rounded-xl bg-cc-tile text-cc-text-muted hover:text-cc-accent hover:bg-cc-tile-hover shadow-md hover:shadow-lg transition-all group border border-cc-tile-border hover:border-cc-accent/30"
          title="Create New Project"
        >
          <Plus size={20} className="group-hover:scale-110 transition-transform" />
        </button>

        <div className={`flex items-center h-20 rounded-xl bg-cc-tile border border-cc-tile-border shadow-md transition-all duration-300 overflow-hidden ${isSearchExpanded ? 'w-32 px-2' : 'w-10 justify-center hover:bg-cc-tile-hover hover:border-cc-accent/30 cursor-pointer'
          }`}>
          {isSearchExpanded ? (
            <div className="flex items-center gap-1.5 w-full">
              <Search size={14} className="text-cc-accent flex-shrink-0" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder="Search..."
                className="flex-1 bg-transparent outline-none text-xs text-cc-text placeholder-cc-text-muted min-w-0"
                onBlur={() => { if (!searchQuery) onSearchToggle(false); }}
                onKeyDown={(e) => { if (e.key === 'Escape') { onSearchChange(''); onSearchToggle(false); } }}
                autoFocus
              />
              {searchQuery && (
                <button onClick={() => { onSearchChange(''); searchInputRef.current?.focus(); }} className="p-0.5 hover:bg-cc-text/10 rounded flex-shrink-0">
                  <X size={10} className="text-cc-text-muted" />
                </button>
              )}
            </div>
          ) : (
            <button onClick={() => onSearchToggle(true)} className="w-full h-full flex items-center justify-center">
              <Search size={16} className="text-cc-text-muted" />
            </button>
          )}
        </div>

        {/* View Mode Toggle - Shows Grid icon to switch to flat list */}
        {onToggleViewMode && (
          <button
            onClick={onToggleViewMode}
            className="flex items-center justify-center w-10 h-20 rounded-xl bg-cc-tile text-cc-text-muted hover:text-cc-accent hover:bg-cc-tile-hover shadow-md hover:shadow-lg transition-all group border border-cc-tile-border hover:border-cc-accent/30"
            title="Switch to flat list view"
          >
            <LayoutGrid size={18} className="group-hover:scale-110 transition-transform" />
          </button>
        )}

        <div
          onClick={handleQuickNotesClick}
          className={`relative w-36 h-20 rounded-xl cursor-pointer transition-all duration-300 overflow-hidden group ${isQuickNotesActive
            ? 'shadow-2xl scale-105'
            : 'shadow-md hover:shadow-xl hover:scale-102'
            }`}
          style={{
            background: isQuickNotesActive
              ? 'linear-gradient(135deg, var(--cc-accent) 0%, color-mix(in srgb, var(--cc-accent) 80%, black) 100%)'
              : 'var(--cc-tile)',
            border: isQuickNotesActive
              ? '2px solid var(--cc-accent)'
              : '1px solid var(--cc-tile-border)',
            boxShadow: isQuickNotesActive
              ? `0 0 ${glowPulse ? '25px' : '15px'} color-mix(in srgb, var(--cc-accent) 50%, transparent), 0 10px 40px -10px rgba(0,0,0,0.3)`
              : undefined,
          }}
        >
          {isQuickNotesActive && (
            <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent pointer-events-none" />
          )}
          <div className="p-2.5 h-full flex flex-col justify-between relative z-10">
            <div className="flex justify-between items-start">
              <span className={`font-semibold text-xs ${isQuickNotesActive ? 'text-white' : 'text-cc-text'}`}>
                Quick Notes
              </span>
              <Sparkles size={14} className={`${isQuickNotesActive ? 'text-white/80' : 'text-cc-accent'} ${isQuickNotesActive ? 'animate-pulse' : ''}`} />
            </div>
            <p className={`text-[10px] ${isQuickNotesActive ? 'text-white/70' : 'text-cc-text-muted'}`}>
              Scratchpad
            </p>
          </div>
        </div>

        <div className="w-px h-14 bg-cc-tile-border/50 ml-1" />
      </div>

      {/* Mobile: Compact controls row */}
      <div className="flex sm:hidden w-full items-center gap-2 px-3 pt-2 pb-1">
        <button
          onClick={onCreateIdea}
          className="flex items-center justify-center w-8 h-8 rounded-lg bg-cc-tile text-cc-text-muted hover:text-cc-accent border border-cc-tile-border"
          title="Create New Project"
        >
          <Plus size={16} />
        </button>

        <button
          onClick={() => onSearchToggle(!isSearchExpanded)}
          className="flex items-center justify-center w-8 h-8 rounded-lg bg-cc-tile text-cc-text-muted hover:text-cc-accent border border-cc-tile-border"
        >
          <Search size={14} />
        </button>

        <div
          onClick={handleQuickNotesClick}
          className={`flex-1 h-8 rounded-lg flex items-center justify-center gap-1.5 cursor-pointer transition-all ${isQuickNotesActive
            ? 'bg-cc-accent text-white shadow-lg'
            : 'bg-cc-tile text-cc-text-muted border border-cc-tile-border'
            }`}
        >
          <Sparkles size={12} />
          <span className="text-xs font-medium">Quick Notes</span>
        </div>

        <div className="text-[10px] text-cc-text-muted bg-cc-tile px-2 py-1 rounded border border-cc-tile-border">
          {totalItems > 0 ? `${currentIndex + 1}/${totalItems}` : '0'}
        </div>
      </div>

      {/* Mobile: Search input (expandable) */}
      {isSearchExpanded && (
        <div className="sm:hidden w-full px-3 pb-1">
          <div className="flex items-center gap-2 h-8 px-2 rounded-lg bg-cc-tile border border-cc-tile-border">
            <Search size={12} className="text-cc-accent" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Search projects..."
              className="flex-1 bg-transparent outline-none text-xs text-cc-text placeholder-cc-text-muted"
              autoFocus
            />
            <button onClick={() => { onSearchChange(''); onSearchToggle(false); }} className="p-1">
              <X size={12} className="text-cc-text-muted" />
            </button>
          </div>
        </div>
      )}

      {/* 3D Carousel Container */}
      <div
        ref={containerRef}
        className="flex-1 w-full h-16 sm:h-full relative overflow-hidden carousel-container"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleMouseDown}
      >
        <div
          className="absolute inset-0 flex items-center justify-center"
          style={{ transformStyle: 'preserve-3d' }}
        >
          {totalItems === 0 ? (
            <div className="text-cc-text-muted text-xs sm:text-sm italic flex items-center gap-2">
              <FileText size={14} />
              <span className="hidden sm:inline">No projects yet. Click + to create one.</span>
              <span className="sm:hidden">No projects</span>
            </div>
          ) : (
            visibleItems.map(({ idea, index, position }) => {
              const isSelected = idea.id === activeIdeaId;
              const isCentered = index === currentIndex;
              const gradient = GRADIENT_COLORS[index % GRADIENT_COLORS.length];

              return (
                <div
                  key={idea.id}
                  style={getTileStyle(position)}
                  onClick={() => handleTileClick(idea, index)}
                  className="cursor-pointer carousel-tile"
                >
                  <div
                    className={`w-36 sm:w-40 h-16 sm:h-20 rounded-xl transition-all duration-300 overflow-hidden relative gpu-accelerated ${isSelected
                      ? 'ring-2 ring-cc-accent shadow-2xl'
                      : isCentered
                        ? 'shadow-xl hover:shadow-2xl'
                        : 'shadow-lg'
                      }`}
                    style={{
                      background: isSelected
                        ? 'linear-gradient(135deg, color-mix(in srgb, var(--cc-accent) 25%, var(--cc-tile)) 0%, var(--cc-tile) 100%)'
                        : 'var(--cc-tile)',
                      border: isSelected
                        ? '2px solid var(--cc-accent)'
                        : '1px solid var(--cc-tile-border)',
                      boxShadow: isSelected
                        ? `0 0 ${glowPulse ? '30px' : '20px'} color-mix(in srgb, var(--cc-accent) 40%, transparent), 0 15px 50px -15px rgba(0,0,0,0.4)`
                        : undefined,
                    }}
                  >
                    {showTileEdgeColors && (
                      <div
                        className="absolute left-0 top-0 bottom-0 w-5 pointer-events-none z-0 transition-opacity duration-300"
                        style={{
                          background: `linear-gradient(to right, ${gradient.from}, ${gradient.to})`,
                          borderTopLeftRadius: 'inherit',
                          borderBottomLeftRadius: 'inherit',
                        }}
                      />
                    )}
                    {isSelected && (
                      <div className="absolute inset-0 bg-gradient-to-br from-cc-accent/10 to-transparent pointer-events-none" />
                    )}

                    {/* Sync status and Edit button - top right */}
                    <div className="absolute top-1.5 right-1.5 z-20 flex items-center gap-1">
                      {idea.googleFolderId && (
                        <span className="p-1 rounded-md bg-cc-accent/15" title="Synced to Google Drive">
                          <Cloud size={10} className="text-cc-accent" />
                        </span>
                      )}
                      {idea.googleDocId && (
                        <span className="p-1 rounded-md bg-cc-accent/15" title="Linked to Google Doc">
                          <Brain size={10} className="text-cc-accent" />
                        </span>
                      )}
                      {isSelected && (
                        <button
                          onClick={(e) => handleEditClick(e, idea.id)}
                          className="p-1 rounded-md bg-cc-accent/90 hover:bg-cc-accent text-white shadow-md transition-all hover:scale-110"
                          title="Open Project"
                        >
                          <Edit3 size={12} />
                        </button>
                      )}
                    </div>

                    <div className="p-2 sm:p-2.5 h-full flex flex-col justify-between relative z-10">
                      <div className="flex-1 min-w-0 pr-6 max-h-[1.875rem] sm:max-h-[2.2rem] overflow-y-auto custom-scrollbar-thin scroll-smooth">
                        <span className={`text-xs sm:text-sm font-medium leading-tight ${isSelected ? 'text-cc-accent' : 'text-cc-text'}`}>
                          {idea.title}
                        </span>
                      </div>
                      <div className="flex items-center gap-1 text-[10px] sm:text-xs text-cc-text-muted">
                        <FileText size={10} />
                        <span className="truncate">
                          {idea.sections?.length || 0} sections
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Navigation Arrows */}
        {totalItems > 1 && (
          <>
            <button
              onClick={goToPrev}
              disabled={currentIndex === 0}
              className={`absolute left-1 sm:left-2 top-1/2 -translate-y-1/2 z-[160] p-1.5 sm:p-2 rounded-full transition-all duration-200 ${currentIndex === 0
                ? 'bg-cc-tile/50 text-cc-text-muted/30 cursor-not-allowed'
                : 'bg-cc-tile/90 hover:bg-cc-accent text-cc-text-muted hover:text-white shadow-lg hover:shadow-xl border border-cc-tile-border hover:border-cc-accent'
                }`}
              title="Previous"
            >
              <ChevronLeft size={16} className="sm:w-5 sm:h-5" />
            </button>
            <button
              onClick={goToNext}
              disabled={currentIndex === maxIndex}
              className={`absolute right-1 sm:right-2 top-1/2 -translate-y-1/2 z-[160] p-1.5 sm:p-2 rounded-full transition-all duration-200 ${currentIndex === maxIndex
                ? 'bg-cc-tile/50 text-cc-text-muted/30 cursor-not-allowed'
                : 'bg-cc-tile/90 hover:bg-cc-accent text-cc-text-muted hover:text-white shadow-lg hover:shadow-xl border border-cc-tile-border hover:border-cc-accent'
                }`}
              title="Next"
            >
              <ChevronRight size={16} className="sm:w-5 sm:h-5" />
            </button>
          </>
        )}

        {/* Navigation Dots */}
        {totalItems > 1 && (
          <div className="absolute bottom-1 left-1/2 -translate-x-1/2 flex items-center gap-1 z-[150]">
            {ideas.slice(0, Math.min(6, ideas.length)).map((idea, idx) => (
              <button
                key={idea.id}
                onClick={() => animateToIndex(idx, true)}
                className={`rounded-full transition-all duration-200 ${idx === currentIndex
                  ? 'bg-cc-accent w-4 h-1.5'
                  : 'bg-cc-text-muted/25 hover:bg-cc-text-muted/40 w-1.5 h-1.5'
                  }`}
              />
            ))}
            {ideas.length > 6 && (
              <span className="text-[9px] text-cc-text-muted ml-0.5">+{ideas.length - 6}</span>
            )}
          </div>
        )}
      </div>

      {/* Position Counter - Desktop only */}
      <div className="hidden sm:flex flex-shrink-0 pr-3 z-[150]">
        <div className="text-xs text-cc-text-muted bg-cc-tile/80 px-2 py-1 rounded-full backdrop-blur-sm border border-cc-tile-border">
          {totalItems > 0 ? `${currentIndex + 1} / ${totalItems}` : '0'}
        </div>
      </div>
    </div>
  );
});
